import React, { useState, useEffect, useContext } from 'react'
import { Container, Row, Col, Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as Yup from 'yup'
import {
  getIndustryMap,
  isAllRequiredFieldsValid,
  knoxRegistrationSolutionName,
  knoxLoginUrls
} from '@srca-b2b/kp5-ui-common/utils/helpers'
import CompanyInfoFieldsGroup from '@srca-b2b/kp5-ui-common/components/CompanyInfoFieldsGroup/CompanyInfoFieldsGroup'
import AddressField from '@srca-b2b/kp5-ui-common/components/AddressField/AddressField'
import MspInfoField from '@srca-b2b/kp5-ui-common/components/MspInfoField/MspInfoField'
import { EnrollmentContext, EnrollmentDispatch } from '../../context/enrollmentContext'
import KP5CONSTANTS from '@srca-b2b/kp5-ui-common/utils/constants'
import getConfig from 'next/config'
import { useTranslation } from 'next-i18next'

import classNames from 'classnames'
import styles from './enrollment.module.scss'

const { publicRuntimeConfig } = getConfig()

const EnrollmentStep3 = () => {
  const [initialFormValues, setInitialFormValues] = useState({})
  const dispatch = useContext(EnrollmentDispatch)
  const state = useContext(EnrollmentContext)
  const industryValues = getIndustryMap()
  const { t } = useTranslation('common')
  const disclaimerInfoLink =
    'https://docs.samsungknox.com/admin/fundamentals/register-for-samsung-knox/before-you-begin'

  const getDynamicValidation = () => {
    const editableFields = {
      officeNumber: Yup.string()
        .matches(KP5CONSTANTS.REGEX.PHONE_NUMBER, t('Valid_phone_number_warning_text'))
        .required('Required')
    }

    if (!state.registrationData.canModifyTenantFields) {
      return editableFields
    }

    let mspInfo = {}

    if (state.productParam === KP5CONSTANTS.REGISTER_PRODUCTS.MANAGED_SERVICE_PROVIDER) {
      mspInfo = {
        supportEmail: Yup.string().email(t('Invalid_email_address')).required(t('Required')),
        supportPhone: Yup.string()
          .matches(KP5CONSTANTS.REGEX.PHONE_NUMBER, t('Valid_phone_number_warning_text'))
          .required(t('Required'))
      }
    }

    return {
      tenantName: Yup.string()
        .required(t('Required'))
        .matches(KP5CONSTANTS.REGEX.WHITESPACE_ONLY, t('Company_name_empty_error')),
      website: Yup.string().matches(
        KP5CONSTANTS.REGEX.WEBSITE_URL,
        `${t('Invalid_website_format')}.`
      ),
      companySize: Yup.string().required(t('Required')),
      industry: Yup.array().of(Yup.string().oneOf(industryValues)).required(t('Required')),
      country: Yup.string().required(t('Required')),
      street1: Yup.string()
        .required(t('Required'))
        .matches(KP5CONSTANTS.REGEX.WHITESPACE_ONLY, t('Address_line1_empty_error')),
      street2: Yup.string(),
      city: Yup.string()
        .required(t('Required'))
        .matches(KP5CONSTANTS.REGEX.WHITESPACE_ONLY, t('City_empty_error')),
      province: Yup.string(),
      zipCode: Yup.string().matches(KP5CONSTANTS.REGEX.WHITESPACE_ONLY, t('Postcode_empty_error')),
      ...editableFields,
      ...mspInfo
    }
  }

  const schema = Yup.object(getDynamicValidation())
  const [formSchema, setFormSchema] = useState(schema)

  useEffect(() => {
    setInitialFormValues(state.registrationData)
    // Need to load legal agreements right away for initial country.
    dispatch({
      type: 'CHANGED_COMPANY_COUNTRY_START',
      payload: { country: state.registrationData.country }
    })
  }, [])

  const onBackClick = (formValues) => {
    console.log('Go to step 2', formValues)
    dispatch({ type: 'GOTO_STEP_2', payload: formValues })
  }

  const onNextClick = (formValues) => {
    console.log('Go to step 4', formValues)
    dispatch({ type: 'GOTO_STEP_4', payload: formValues })
  }

  const onFormChange = (e) => {
    if (e.target.name === 'country') {
      dispatch({ type: 'CHANGED_COMPANY_COUNTRY_START', payload: { country: e.target.value } })
    }
  }

  const varnish_urls = knoxLoginUrls()

  let product_url_idx = ''
  let supportedLocationsProductTitle = state.productTitle

  const knoxSuiteParams = [
    KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE,
    KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE_ALIAS,
    KP5CONSTANTS.REGISTER_PRODUCTS.MANAGE,
    KP5CONSTANTS.REGISTER_PRODUCTS.EFOTA_ONE,
    KP5CONSTANTS.REGISTER_PRODUCTS.CLOUD_SERVICES,
    KP5CONSTANTS.REGISTER_PRODUCTS.SUITE
  ]
  if (knoxSuiteParams.includes(state.productParam)) {
    supportedLocationsProductTitle = knoxRegistrationSolutionName(
      KP5CONSTANTS.REGISTER_PRODUCTS.CLOUD_SERVICES
    )
  }

  switch (state.productParam) {
    case KP5CONSTANTS.REGISTER_PRODUCTS.CONFIGURATOR:
      product_url_idx = 'knoxConfigureUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.MOBILE_ENROLLMENT:
      product_url_idx = 'knoxMobileEnrollmentUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.GUARD:
      product_url_idx = 'knoxGuardUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.DEPLOYMENT_PROGRAM:
      product_url_idx = 'forResellersUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE:
      product_url_idx = 'knoxCaptureUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.CARE_PLUS:
      product_url_idx = 'samsungCarePlusUrl'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.MANAGED_SERVICE_PROVIDER:
      product_url_idx = 'forMSPs'
      break
    case KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE:
    case KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE_ALIAS:
    case KP5CONSTANTS.REGISTER_PRODUCTS.MANAGE:
    case KP5CONSTANTS.REGISTER_PRODUCTS.EFOTA_ONE:
    case KP5CONSTANTS.REGISTER_PRODUCTS.CLOUD_SERVICES:
    case KP5CONSTANTS.REGISTER_PRODUCTS.SUITE:
    default:
      product_url_idx = 'itSolutionsUrl'
      break
  }
  let supportedLocationsUrl = ''

  const varnishKey = product_url_idx === 'forMSPs' ? 'partners' : 'solutions'

  if (product_url_idx && varnish_urls[varnishKey][product_url_idx]) {
    supportedLocationsUrl =
      varnishKey === 'partners'
        ? `${varnish_urls.partners[product_url_idx]}/supported-locations`
        : `${publicRuntimeConfig.kp4BaseUrl}${varnish_urls.solutions[product_url_idx]}/supported-locations`
  }

  return (
    <Container id="enrollment-step3" className={styles.enrollmentStep3}>
      <h4 className={classNames('text-center mb-3 mb-md-4', styles.headerTitle)}>
        {t('Company_information')}
      </h4>
      <p className="lead text-center pb-md-2 mb-md-4">{t('This_information_will_be_used_text')}</p>
      <Formik
        validationSchema={formSchema}
        onSubmit={onNextClick}
        initialValues={{ ...initialFormValues }}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleSubmit, handleChange, setFieldValue, values, isSubmitting }) => (
          <Form noValidate onSubmit={handleSubmit} onChange={onFormChange}>
            <div className={styles.formBodyContainer}>
              <div className={classNames(styles.formBody, 'flex-row')}>
                <Col
                  md={6}
                  className={classNames(styles.endBorderedContainer, 'pb-4 pb-md-0 mb-4 mb-md-0')}
                >
                  <CompanyInfoFieldsGroup
                    industryValues={industryValues}
                    values={values}
                    canEdit={state.registrationData.canModifyTenantFields}
                    columnSize={12}
                  />
                </Col>
                <Col md={6}>
                  <AddressField
                    setFormSchema={setFormSchema}
                    setFieldValue={setFieldValue}
                    formSchema={schema}
                    productTitle={supportedLocationsProductTitle}
                    supportedCountries={state.registrationData.supportedCountries}
                    supportedLocationsUrl={supportedLocationsUrl}
                    canEditCountry={state.registrationData.canModifyCountry}
                    canEdit={state.registrationData.canModifyTenantFields}
                    canEditProvince={state.registrationData.canModifyProvince}
                    canEditZipcode={state.registrationData.canModifyZipcode}
                    columnSize={12}
                  />
                </Col>
                {state.productParam === KP5CONSTANTS.REGISTER_PRODUCTS.MANAGED_SERVICE_PROVIDER && (
                  <>
                    <Col md={12}>
                      <p className="lead text-center pb-2 my-4">
                        {t('For_MSPs_this_information_text')}
                      </p>
                    </Col>
                    <MspInfoField
                      setFormSchema={setFormSchema}
                      canEdit={state.registrationData.canModifyTenantFields}
                      columnSize={6}
                    />
                  </>
                )}
                <p className={styles.disclaimerText}>
                  <strong>{t('Disclaimer')}</strong>: {t('You_can_only_manage_text')}&nbsp;
                  <a
                    id="ga-kp-getstarted-signin-companyinfo-disclaimer-learnmore"
                    href={disclaimerInfoLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('Learn_more')}
                  </a>
                </p>
              </div>
            </div>
            <Row className={styles.enrollmentButtonWrapper}>
              <Col md="auto">
                <Button
                  id="ga-registration-companyinfo-back"
                  onClick={() => {
                    onBackClick(values)
                  }}
                  disabled={isSubmitting}
                  className="btn-registration-prev btn-outline-primary arrow-left"
                >
                  {t('Back')}
                  <span className={styles.changePageTitle}>: {t('Samsung_Account')}</span>
                </Button>
              </Col>
              <Col md="auto">
                <Button
                  type="submit"
                  id="ga-registration-companyinfo-next"
                  className={'btn-registration-next arrow-right'}
                  disabled={
                    isSubmitting ||
                    isAllRequiredFieldsValid(formSchema, values) ||
                    state.changeCountryStart
                  }
                >
                  {t('Next')}
                  <span className={styles.changePageTitle}>: {t('Agreement')}</span>
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default EnrollmentStep3
